/**
 * Vertical drags on the timeline (1.7.4, docs/dev/editor-1.7.4-build.md "Dragging between tracks"): which video,
 * audio or graphics track a drag lands on, whether it is past the last track (the dashed "New track" lane, a track
 * made on drop), and which op the drop is. Pure; the ops are ops.moveToVideoTrack and ops.moveItems.
 */
import {
  MAX_AUDIO_TRACKS, MAX_GRAPHIC_TRACKS, MAX_LAYERS, allAudioTracks, audioTrackOf, graphicTrackCount, graphicTrackOf,
  type ItemRef, type SequenceDocument, type VideoTrackTarget,
} from '@prism/contracts';

/** The video lane of a picture: V1 = 0, layers[i] = i + 1; null for other items. */
export function videoLaneOf(doc: SequenceDocument, ref: ItemRef): number | null {
  if (ref.kind === 'video') return doc.video.some((c) => c.id === ref.id) ? 0 : null;
  if (ref.kind !== 'layer') return null;
  const i = (doc.layers ?? []).findIndex((l) => l.clips.some((c) => c.id === ref.id));
  return i < 0 ? null : i + 1;
}

/** Where a picture dragged `up` lanes (negative: down) from `fromLane` lands. */
export interface VideoDrop { lane: number; target: VideoTrackTarget; newTrack: boolean; changed: boolean }

export function videoDrop(doc: SequenceDocument, fromLane: number, up: number): VideoDrop {
  const layers = doc.layers ?? [];
  let lane = Math.max(0, fromLane + up);
  let newTrack = false;
  if (lane > layers.length) {
    if (layers.length < MAX_LAYERS) { lane = layers.length + 1; newTrack = true; } else lane = layers.length;
  }
  const target: VideoTrackTarget = lane === 0 ? 'v1' : newTrack ? 'new' : { layer: layers[lane - 1]!.id };
  return { lane, target, newTrack, changed: lane !== fromLane };
}

/** Where an audio clip dragged `down` lanes from the track at `fromIndex` (0 = A2) lands; past A8 nothing is made. */
export interface AudioDrop { index: number; newTrack: boolean; delta: number }

export function audioDrop(doc: SequenceDocument, fromIndex: number, down: number): AudioDrop {
  const count = allAudioTracks(doc).length;
  // A2 plus up to MAX_AUDIO_TRACKS more (A8).
  const index = Math.max(0, Math.min(fromIndex + down, MAX_AUDIO_TRACKS));
  return { index, newTrack: index > count - 1, delta: index - fromIndex };
}

/** The index (0 = A2) of an audio clip's track; -1 when it is not on one. */
export function audioIndexOf(doc: SequenceDocument, id: string): number {
  const t = audioTrackOf(doc, id);
  return allAudioTracks(doc).findIndex((x) => x.id === t);
}

/** Where a graphic dragged `up` lanes from graphics track `from` lands; past GFX 3 it stays on the top track. */
export interface GraphicDrop { track: number; newTrack: boolean; delta: number }

export function graphicDrop(doc: SequenceDocument, from: number, up: number): GraphicDrop {
  const count = graphicTrackCount(doc);
  const track = Math.max(0, Math.min(from + up, MAX_GRAPHIC_TRACKS - 1));
  return { track, newTrack: track > count - 1, delta: track - from };
}

export const graphicTrackIndex = (doc: SequenceDocument, id: string): number => {
  const g = doc.graphics.find((x) => x.id === id);
  return g ? graphicTrackOf(g) : 0;
};

/**
 * The items of a move with the grabbed picture first (ops.moveToVideoTrack takes the first 'video' or 'layer' item
 * as the one that goes to `target`).
 */
export function primaryFirst(items: readonly ItemRef[], grabbed: ItemRef | undefined): ItemRef[] {
  if (!grabbed || (grabbed.kind !== 'video' && grabbed.kind !== 'layer')) return [...items];
  const rest = items.filter((r) => !(r.kind === grabbed.kind && r.id === grabbed.id));
  return [grabbed, ...rest];
}

/** The toast of a move's notes (one sentence each), or null when there is nothing to say. */
export const notesToast = (notes: readonly string[]): string | null => (notes.length ? notes.join(' ') : null);
